import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Lifebuoy, CaretDown, CaretRight, ArrowRight, CheckCircle, WarningCircle, Trash } from "@phosphor-icons/react";

const BUCKET_CLS = {
  deliverable: "bg-emerald-50 text-emerald-700 border-emerald-200",
  risky: "bg-amber-50 text-amber-700 border-amber-200",
  undeliverable: "bg-rose-50 text-rose-700 border-rose-200",
  unverified: "bg-zinc-50 text-zinc-500 border-zinc-200",
};

export const RescueCard = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [selDead, setSelDead] = useState(new Set());
  const [selProps, setSelProps] = useState(new Set());
  const [finding, setFinding] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [applying, setApplying] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const verifyingRef = useRef(false);

  const verifyLoop = async () => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    try {
      let r = { remaining: 1 };
      while (r.remaining > 0) {
        r = await api.rescueVerifyPending({ limit: 3 });
        setVerifying(r);
      }
    } catch (e) { toast.error(fmtErr(e)); }
    verifyingRef.current = false;
    setVerifying(null);
  };

  const load = () => api.rescueScan().then(d => {
    setData(d);
    setSelProps(new Set(d.proposals
      .filter(p => p.verification?.bucket === "deliverable" && (!p.moved || p.icp_in_scope))
      .map(p => p.id)));
    if (d.proposals.some(p => p.status === "proposed" && !p.verification) && !verifyingRef.current) {
      verifyLoop().then(() => api.rescueScan().then(d2 => {
        setData(d2);
        setSelProps(new Set(d2.proposals
          .filter(p => p.verification?.bucket === "deliverable" && (!p.moved || p.icp_in_scope))
          .map(p => p.id)));
      }));
    }
  }).catch(e => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const dead = data.contacts.filter(c => !c.rescue_status);
  const attemptable = dead.filter(c => c.has_name);
  const proposals = data.proposals;

  const findSelected = async () => {
    const ids = [...selDead];
    if (!ids.length) return;
    if (!window.confirm(`Check ${ids.length} contact${ids.length === 1 ? "" : "s"} against Apollo? The search itself is FREE — a credit (~1) is only spent to reveal the new address of someone who has genuinely moved company, plus 0.5 Hunter credit to verify each proposal.`)) return;
    setFinding(true); setProgress({ done: 0, total: ids.length });
    let credits = 0, found = 0, failed = 0, firstErr = "";
    for (let i = 0; i < ids.length; i += 10) {
      try {
        const r = await api.rescueFind({ contact_ids: ids.slice(i, i + 10) });
        credits += r.apollo_credits || 0;
        found += r.proposals.filter(p => p.status === "proposed").length;
      } catch (e) {
        failed += 1;
        if (!firstErr) firstErr = fmtErr(e);
      }
      setProgress(p => ({ ...p, done: Math.min(p.total, i + 10) }));
    }
    if (failed) toast.warning(`${failed} batch${failed === 1 ? "" : "es"} failed — ${firstErr}. Anything already found is saved; just rerun for the rest.`);
    toast.success(`Search done — ${found} new address${found === 1 ? "" : "es"} proposed (${credits} Apollo credit${credits === 1 ? "" : "s"} used). Verifying them now…`);
    setSelDead(new Set()); setFinding(false);
    await load();
  };

  const apply = async () => {
    const ids = [...selProps];
    if (!ids.length) return;
    setApplying(true);
    try {
      const r = await api.rescueApply({ rescue_ids: ids });
      toast.success(`${r.applied} contact${r.applied === 1 ? "" : "s"} updated with a working address${r.skipped.length ? ` · ${r.skipped.length} skipped` : ""}`);
    } catch (e) { toast.error(fmtErr(e)); }
    setApplying(false); await load();
  };

  const dismiss = async () => {
    const ids = [...selProps];
    if (!ids.length) return;
    try { await api.rescueDismiss({ rescue_ids: ids }); } catch (e) { toast.error(fmtErr(e)); }
    await load();
  };

  const toggleProp = (id) => setSelProps(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="rounded-lg border border-sky-200 bg-white" data-testid="rescue-card">
      <div className="p-3 cursor-pointer" onClick={() => setOpen(v => !v)} data-testid="rescue-toggle">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
          <Lifebuoy size={15} className="text-sky-600" /> Undeliverable rescue — find where dead contacts went
          <span className="ml-2 text-xs font-normal text-zinc-500">
            {data.counts.total} dead address{data.counts.total === 1 ? "" : "es"}
            {proposals.length > 0 && <span className="text-sky-700 font-medium"> · {proposals.length} proposal{proposals.length === 1 ? "" : "s"} awaiting review</span>}
            {data.counts.applied > 0 && <span className="text-emerald-700"> · {data.counts.applied} rescued</span>}
          </span>
        </div>
        <p className="text-xs text-zinc-500 mt-0.5 ml-[18px]">
          Apollo tracks people across job moves: a FREE Apollo search first checks where each dead contact is now (matched by LinkedIn or exact name). Credits are only spent revealing the new address of people who genuinely moved company — never on "still there with the same dead address" results. Every proposal is auto-verified with Hunter and AI-checked against the Medicys ICP; you approve every change and the old address is kept in the contact's history.
        </p>
      </div>

      {open && (
        <div className="border-t border-sky-100">
          {proposals.length > 0 && (
            <div className="border-b border-zinc-100">
              <div className="px-3 py-2 text-xs font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-2">
                Proposed fixes — tick &amp; apply
                {verifying && <span className="normal-case font-normal text-sky-600 animate-pulse" data-testid="rescue-verifying">verifying addresses with Hunter… {verifying.remaining} left</span>}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {proposals.map(p => (
                    <tr key={p.id} className="border-t border-zinc-100" data-testid={`rescue-prop-${p.id}`}>
                      <td className="px-3 py-2 w-8"><input type="checkbox" checked={selProps.has(p.id)} onChange={() => toggleProp(p.id)} data-testid={`rescue-prop-check-${p.id}`} /></td>
                      <td className="px-3 py-2 font-medium text-zinc-900 whitespace-nowrap">{p.name}</td>
                      <td className="px-3 py-2 font-mono text-[12px]">
                        <span className="line-through text-zinc-400">{p.old_email}</span>
                        <ArrowRight size={11} className="inline mx-1.5 text-zinc-400" />
                        <span className="text-zinc-900">{p.proposed_email}</span>
                      </td>
                      <td className="px-3 py-2">
                        {p.verification
                          ? <span className={`text-[11px] px-1.5 py-0.5 rounded border ${BUCKET_CLS[p.verification.bucket] || BUCKET_CLS.unverified}`}>{p.verification.bucket || "unverified"}{p.verification.score != null ? ` ${p.verification.score}` : ""}</span>
                          : <span className="text-[11px] px-1.5 py-0.5 rounded border border-sky-200 bg-sky-50 text-sky-700 animate-pulse">verifying…</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-600">
                        {p.moved ? (
                          <span>
                            Moved → <span className="font-medium text-zinc-900">{p.new_company || p.proposed_email.split("@")[1]}</span>
                            {p.icp_in_scope === true && <span className="text-emerald-700"> · <CheckCircle size={11} className="inline" /> ICP in scope</span>}
                            {p.icp_in_scope === false && <span className="text-amber-600"> · <WarningCircle size={11} className="inline" /> out of scope</span>}
                            {p.icp_reason && <span className="text-zinc-400" title={p.icp_reason}> — {p.icp_reason}</span>}
                          </span>
                        ) : <span>Same company{p.new_title ? ` · now ${p.new_title}` : ""}</span>}
                        {p.moved && p.new_title && <div className="text-zinc-400">{p.new_title}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2.5 flex items-center gap-2">
                <button onClick={apply} disabled={applying || selProps.size === 0} data-testid="rescue-apply-btn"
                  className="h-8 px-3.5 rounded-md bg-sky-700 text-white text-sm font-medium hover:bg-sky-800 disabled:opacity-40">
                  {applying ? "Applying…" : `Apply ${selProps.size} fix${selProps.size === 1 ? "" : "es"}`}
                </button>
                <button onClick={dismiss} disabled={selProps.size === 0} data-testid="rescue-dismiss-btn"
                  className="h-8 px-3 rounded-md border border-zinc-200 text-sm text-zinc-600 hover:border-zinc-300 disabled:opacity-40 inline-flex items-center gap-1">
                  <Trash size={13} /> Dismiss selected
                </button>
                <span className="text-xs text-zinc-400">deliverable + in-scope proposals are pre-ticked</span>
              </div>
            </div>
          )}

          <div className="px-3 py-2 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            <span><span className="font-medium text-zinc-800">{attemptable.length}</span> not yet searched</span>
            {data.counts.no_result > 0 && <span>· {data.counts.no_result} searched, nothing found</span>}
            {dead.length > attemptable.length && <span>· {dead.length - attemptable.length} missing a full name (can't match)</span>}
            <span className="ml-auto flex items-center gap-1.5">
              <button type="button" data-testid="rescue-select-all" onClick={() => setSelDead(new Set(attemptable.map(c => c.id)))}
                className="px-2 py-1 rounded border border-zinc-200 hover:border-zinc-300">Select all {attemptable.length}</button>
              <button type="button" onClick={() => setSelDead(new Set())} className="px-2 py-1 rounded border border-zinc-200 hover:border-zinc-300">None</button>
            </span>
          </div>
          <div className="max-h-72 overflow-auto border-t border-zinc-100">
            <table className="w-full text-sm">
              <tbody>
                {attemptable.map((c, i) => (
                  <tr key={c.id} className="border-b border-zinc-50" data-testid={`rescue-dead-${i}`}>
                    <td className="px-3 py-1.5 w-8"><input type="checkbox" checked={selDead.has(c.id)}
                      onChange={() => setSelDead(prev => { const n = new Set(prev); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; })} /></td>
                    <td className="px-3 py-1.5 text-zinc-900 whitespace-nowrap">{c.first_name} {c.last_name}</td>
                    <td className="px-3 py-1.5 font-mono text-[12px] text-rose-600">{c.email}</td>
                    <td className="px-3 py-1.5 text-zinc-500 max-w-[200px] truncate">{c.company}</td>
                    <td className="px-3 py-1.5 text-zinc-400 text-xs max-w-[220px] truncate">{c.job_title}</td>
                  </tr>
                ))}
                {attemptable.length === 0 && <tr><td className="p-6 text-center text-sm text-zinc-400">Nothing left to search — every dead contact has been attempted.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2.5 flex items-center gap-3 border-t border-zinc-100">
            <button onClick={findSelected} disabled={finding || selDead.size === 0} data-testid="rescue-find-btn"
              className="h-9 px-4 rounded-md bg-zinc-950 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1.5">
              <Lifebuoy size={14} /> {finding ? `Searching… ${progress.done}/${progress.total}` : `Find new addresses for ${selDead.size || "…"} selected`}
            </button>
            <span className="text-xs text-zinc-400">free Apollo search first — a credit is only spent revealing someone who genuinely moved company (+0.5 Hunter to verify) · already-searched contacts are never charged twice</span>
          </div>
        </div>
      )}
    </div>
  );
};
